import React from 'react';
import { Link } from 'react-router-dom';
import { SiteHeader } from '../../components/bx';
import '../../styles/bx.css';
import { OfferItem, OfferItemType } from '../../services/offersApi';
import './offers.css';


export function OfferShell({ children, backTo = '/offers', backLabel = 'All offers' }: { children: React.ReactNode; backTo?: string; backLabel?: string }) {
  return (
    <div className="mo">
      {/* The header already links to all offers; only show a back link for deeper pages. */}
      <SiteHeader right={backTo !== '/offers' ? <Link to={backTo} className="mo-header-link">{backLabel}</Link> : undefined} />
      <main className="mo-main">{children}</main>
    </div>
  );
}

const TYPE_EMOJI: Record<OfferItemType, string> = {
  breakfast: '🍳',
  lunch: '🍛',
  dinner: '🍲',
  water: '💧',
  drinks: '🧃',
  toppings: '🍗',
};

export function ItemThumb({ item }: { item?: Pick<OfferItem, 'image_url' | 'type' | 'name'> }) {
  return (
    <span className="mo-thumb" aria-hidden="true">
      {item?.image_url ? <img src={item.image_url} alt="" loading="lazy" /> : TYPE_EMOJI[item?.type as OfferItemType] || '🍽️'}
    </span>
  );
}

export function Stepper({ value, min = 0, max = 10, onChange, label }: { value: number; min?: number; max?: number; onChange: (next: number) => void; label: string }) {
  return (
    <span className="mo-stepper" role="group" aria-label={`${label} quantity`}>
      <button type="button" aria-label={value - 1 < 1 ? `Remove ${label}` : `Decrease ${label}`} onClick={() => onChange(value - 1)} disabled={value <= min}>
        {value <= 1 ? '×' : '−'}
      </button>
      <span aria-live="polite">{value}</span>
      <button type="button" aria-label={`Increase ${label}`} onClick={() => onChange(value + 1)} disabled={value >= max}>
        +
      </button>
    </span>
  );
}

export function OfferState({ icon, title, message, action }: { icon: string; title: string; message?: string; action?: React.ReactNode }) {
  return (
    <div className="mo-state">
      <div className="mo-state-icon" aria-hidden="true">{icon}</div>
      <h2>{title}</h2>
      {message ? <p>{message}</p> : null}
      {action}
    </div>
  );
}

// ---------- order draft persistence (survives refresh and the Paystack redirect) ----------
export interface OfferDraft {
  slug: string;
  planId: string;
  schedule: import('../../services/offersApi').OfferSchedule;
  fulfilment: 'delivery' | 'pickup';
  customer: { name: string; email: string; phone: string; address: string };
  note: string;
  reference?: string;
  quotedTotal?: number;
}

const draftKey = (slug: string) => `boqbox_offer_draft_${slug}`;

export const loadDraft = (slug: string): OfferDraft | null => {
  try {
    const raw = localStorage.getItem(draftKey(slug));
    return raw ? (JSON.parse(raw) as OfferDraft) : null;
  } catch {
    return null;
  }
};

export const saveDraft = (draft: OfferDraft) => {
  try {
    localStorage.setItem(draftKey(draft.slug), JSON.stringify(draft));
  } catch {
    // storage unavailable: the flow still works for this page view
  }
};

export const clearDraft = (slug: string) => {
  try {
    localStorage.removeItem(draftKey(slug));
  } catch {
    // ignore
  }
};
